import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import InfoIcon from '@mui/icons-material/Info';
import { useSelector } from 'react-redux';
import moment from 'moment';
import OutlinedSelect from '../../../../common/OutlinedSelect';
import { renderHiddenInput } from '../../../../common/FormHiddenField';

const returnMonths = (frequency) => ({
  2: 'Semestre',
  3: 'Trimestre',
  5: 'Mes',
})[frequency];

const returnNumberOfMonth = (frequency) => ({
  2: 6,
  3: 3,
  5: 1,
})[frequency];

export default function BankDebitPayment({ effectiveDate = '', deductibleTotal }) {
  const { control, setValue, watch, formState: { errors } } = useFormContext();
  const { response: { catalogs = [] } } = useSelector((state) => state.catalogs);
  const application = useSelector((state) => state.application);
  const banks = catalogs?.find((c) => c.id === 'InstitucionFinancieraDebito')?.catalogue;
  const frequencyWatch = watch('debitPayment.paymentFrequency');
  const [freqDebitAmount, setFreqDebitAmount] = React.useState(false);

  const onChangeBankDescription = (event) => {
    const bank = banks.find((b) => b.id === event.target.value);
    setValue('debitPayment.bankDescription', bank?.description || '');
    return event.target.value;
  };

  useEffect(() => {
    const { billingData } = application.requestData;
    setValue('debitPayment.accountHolder', `${billingData?.name} ${billingData?.lastName}`);
  }, [application.requestData, setValue]);


  useEffect(() => {
    if (frequencyWatch) {
      const debitAmount = returnNumberOfMonth(frequencyWatch) * deductibleTotal;
      setFreqDebitAmount(Math.round(debitAmount * 100) / 100);
    }
  }, [frequencyWatch]);

  const frecuenciaRef = useRef();
  const numeroRef = useRef();
  const bancoRef = useRef();
  const tipoRef = useRef();

  return (
    <>
      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          error={errors?.debitPayment?.hasOwnProperty('paymentFrequency')}
        >
          <InputLabel id="debit-frequency-select">FRECUENCIA DE PAGO</InputLabel>
          <Controller
            name="debitPayment.paymentFrequency"
            control={control}
            defaultValue=""
            render={({ field: { ref, ...fieldProps } }) => (
              <OutlinedSelect {...fieldProps} inputRef={ref} labelId="debit-frequency-select" label="FRECUENCIA DE PAGO">
                <MenuItem value=""><em>Seleccione..</em></MenuItem>
                <MenuItem value="2">Semestral</MenuItem>
                <MenuItem value="3">Trimestral</MenuItem>
                <MenuItem value="5">Mensual</MenuItem>
              </OutlinedSelect>
            )}
          />
          <FormHelperText>
            {errors.debitPayment?.paymentFrequency?.message}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="debitPayment.accountHolder"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="TITULAR DE LA CUENTA"
              InputProps={{ readOnly: true }}
              error={errors?.debitPayment?.hasOwnProperty('accountHolder')}
              helperText={errors.debitPayment?.accountHolder?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="debitPayment.bankAccount"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              inputRef={numeroRef}
              onFocus={() => numeroRef.current.focus()}
              fullWidth
              label="NUMERO DE CUENTA"
              inputProps={{ maxLength: 16 }}
              error={errors?.debitPayment?.hasOwnProperty('bankAccount')}
              helperText={errors.debitPayment?.bankAccount?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          error={errors?.debitPayment?.hasOwnProperty('bankCode')}
        >
          <InputLabel id="debit-bank-select">INSTITUCIÓN FINANCIERA</InputLabel>
          <Controller
            name="debitPayment.bankCode"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OutlinedSelect
                {...field}
                labelId="debit-bank-select"
                label="INSTITUCIÓN FINANCIERA"
                inputRef={bancoRef}
                onFocus={() => bancoRef.current.focus()}
                onChange={(event) => { field.onChange(onChangeBankDescription(event)); }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {
                  banks?.map((item) => (
                    <MenuItem
                      key={item.id}
                      value={item.id}
                    >
                      {item.description}
                    </MenuItem>
                  ))
                }
              </OutlinedSelect>
            )}
          />
          <FormHelperText>
            {errors.debitPayment?.bankCode?.message}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          error={errors?.debitPayment?.hasOwnProperty('accountType')}
        >
          <InputLabel id="debit-account-type-select">TIPO DE CUENTA</InputLabel>
          <Controller
            name="debitPayment.accountType"
            control={control}
            defaultValue=""
            render={({ field: { ref, ...fieldProps } }) => (
              <OutlinedSelect {...fieldProps} inputRef={ref} labelId="debit-account-type-select" label="TIPO DE CUENTA">
                <MenuItem value=""><em>Seleccione...</em></MenuItem>
                <MenuItem value="1">Ahorros</MenuItem>
                <MenuItem value="2">Corriente</MenuItem>
              </OutlinedSelect>
            )}
          />
          <FormHelperText>
            {errors.debitPayment?.accountType?.message}
          </FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        {frequencyWatch && (
          <Grid container spacing={1}>
            {effectiveDate && (
              <>
                <Grid item><InfoIcon color="error" /></Grid>
                <Grid item>
                  <Typography
                    component="p"
                    color="textSecondary"
                  >
                    {`El dia
                                     ${moment(effectiveDate).format('D')}
                                      de cada ${returnMonths(frequencyWatch) || ''} 
                                      se debitaran $${freqDebitAmount} de su cuenta.`}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Grid>
      <Controller
        name="debitPayment.bankDescription"
        control={control}
        defaultValue=""
        render={renderHiddenInput}
      />
    </>
  );
}

BankDebitPayment.propTypes = {
  effectiveDate: PropTypes.string,
  deductibleTotal: PropTypes.number.isRequired,
};
